import { useEffect, useRef } from 'react';
import {
  endTelemetrySession,
  getSessionTimeoutMs,
  getTelemetrySessionSnapshot,
  startTelemetrySession,
  touchTelemetrySession,
  type TelemetryIdentity,
} from '../services/usageService';

export function usePlatformSessionTelemetry(identity: TelemetryIdentity | null | undefined): void {
  const identityRef = useRef<TelemetryIdentity | null | undefined>(identity);
  identityRef.current = identity;

  useEffect(() => {
    if (!identity) return;

    let ended = false;
    let timeoutId: number | null = null;

    const scheduleTimeout = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      const timeoutMs = getSessionTimeoutMs();
      timeoutId = window.setTimeout(() => {
        const session = getTelemetrySessionSnapshot();
        if (!session) return;

        const idleMs = Date.now() - new Date(session.lastActivityAt).getTime();
        if (idleMs >= timeoutMs) {
          ended = true;
          endTelemetrySession(identityRef.current, 'session_timeout');
          return;
        }

        scheduleTimeout();
      }, timeoutMs);
    };

    const markActivity = () => {
      if (ended) {
        ended = false;
        startTelemetrySession(identityRef.current);
      }
      touchTelemetrySession();
      scheduleTimeout();
    };

    const handlePageHide = () => {
      if (ended) return;
      ended = true;
      touchTelemetrySession();
      endTelemetrySession(identityRef.current, 'session_end', 'unload');
    };

    startTelemetrySession(identity);
    scheduleTimeout();

    window.addEventListener('click', markActivity, true);
    window.addEventListener('keydown', markActivity, true);
    window.addEventListener('focus', markActivity, true);
    document.addEventListener('visibilitychange', markActivity);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      window.removeEventListener('click', markActivity, true);
      window.removeEventListener('keydown', markActivity, true);
      window.removeEventListener('focus', markActivity, true);
      document.removeEventListener('visibilitychange', markActivity);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [identity]);
}
